import { FormFieldConfig } from '../../features/ui-config'
import i18n from '../i18n'
import { getConfigTranslation } from './getConfigTranslation'
import { isEmpty } from './isEmpty'

/**
 * An object representing a field and its validation configuration.
 * @typedef {Object} FieldToValidate
 * @property {string} fieldName - The name of the field to validate.
 * @property {Object} config - The validation configuration for the field.
 */
export type FieldToValidate = {
  fieldName: string
  config: FormFieldConfig
}

/**
 * Validates the fields in `data` based on the provided validation configuration and stores validation errors in `errors`.
 *
 * @param {FieldToValidate[]} fieldsToValidate - An array of fields and their validation configuration.
 * @param {Object} data - An object containing the data to be validated.
 * @param {Object} errors - An object where validation errors will be stored.
 */
export function validateFormikFields(
  fieldsToValidate: FieldToValidate[],
  data: any,
  errors: any,
) {
  fieldsToValidate.forEach(field => {
    const name = field.fieldName
    const value = data[name as keyof typeof data]
    const config = field.config

    const fieldError = validateField(value, config)

    if (fieldError) {
      errors[name as keyof typeof data] = fieldError
    }
  })
}

/**
 * Validates the value of a field based on the provided validation configuration.
 *
 * @param {*} value - The value to be validated.
 * @param {Object} validationConfig - The validation configuration for the field.
 * @returns {string|null} - Returns an error message if validation fails, otherwise returns `null`.
 */
function validateField(value: any, config: FormFieldConfig) {
  // If field is deactivated don't check it
  const isDisabled = config.enabled === false
  const isReadonly = config.readonly === true
  if (isDisabled || isReadonly) return null

  // The field is required but has not been filled
  if (config?.validation && config?.validation.isRequired && isEmpty(value)) {
    return i18n.t(`common.errors.required`)
  }

  // The field is filled but does not match the correct format
  if (
    value &&
    config?.validation &&
    config?.validation.regex &&
    !new RegExp(config.validation.regex).test(value)
  ) {
    return getConfigTranslation(config.validation.regexErrorMessage)
  }

  return null
}
